import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import api from "../../services/api";
import Loader from "../../components/Loader";
import Table from "../../components/Table";
import Pagination from "../../components/Pagination";

const HEADER = [
  { title: "#", key: "row_number" },
  { title: "Demandeur", key: "user_from_first_name" },
  { title: "Personne contactée", key: "user_to_first_name" },
  { title: "Entreprise", key: "company_name" },
  { title: "Type d'annonce", key: "company_annonce_type" },
  { title: "Titre d'annonce", key: "company_annonce_title" },
  { title: "Investisseur", key: "investor_name" },
  { title: "NDA Demandeur", key: "nda_user_from_signed" },
  { title: "NDA Contacté", key: "nda_user_to_signed" },
  { title: "Date de création", key: "created_at" },
  { title: "Dernière MAJ", key: "updated_at" },
  { title: "Statut", key: "status" },
];

const List = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "",
    company_annonce_type: searchParams.get("company_annonce_type") || "",
    nda_user_from_signed: searchParams.get("nda_user_from_signed") || "",
    nda_user_to_signed: searchParams.get("nda_user_to_signed") || "",
    page: Number(searchParams.get("page")) || 1,
    limit: Number(searchParams.get("limit")) || 50,
    sort: { created_at: -1 },
  });

  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let query = {};
        if (filters.status) query.status = filters.status;
        if (filters.company_annonce_type) query.company_annonce_type = filters.company_annonce_type;
        if (filters.nda_user_from_signed) query.nda_user_from_signed = filters.nda_user_from_signed === "true";
        if (filters.nda_user_to_signed) query.nda_user_to_signed = filters.nda_user_to_signed === "true";
        if (filters.page) query.skip = (filters.page - 1) * filters.limit;
        if (filters.limit) query.limit = filters.limit;
        if (filters.sort) query.sort = filters.sort;

        setSearchParams(filters);

        const res = await api.post("/discussion/search", query);
        if (!res.ok) throw res;
        setDiscussions(res.data);
        setTotal(res.total);
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la récupération des données");
      }
      setLoading(false);
    };

    fetchData();
  }, [filters]);

  const formatAnnonceType = (type) => {
    switch (type) {
      case "levee":
        return "Levée de fonds";
      case "cession":
        return "Cession";
      default:
        return type || "-";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const NDAStatus = ({ signed }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${signed ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
      {signed ? "✓ Signé" : "✗ Non signé"}
    </span>
  );

  return (
    <div className="p-8">
      <header className="w-full">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-black mb-2">Discussions</h1>
            <p className="text-base text-gray-500">Voici la liste des discussions</p>
          </div>
          <div className="bg-primary text-white px-4 py-2 rounded-lg">
            <span className="text-sm font-medium">Total: {total}</span>
          </div>
        </div>
      </header>
      <div className="flex flex-wrap gap-4 items-center">
        <select
          className="block w-fit px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
          value={filters.status}
          onChange={(event) => setFilters((prevFilters) => ({ ...prevFilters, status: event.target.value, page: 1 }))}>
          <option value="">Tous les statuts</option>
          <option value="PENDING">En attente</option>
          <option value="ACCEPTED">Accepté</option>
          <option value="ONGOING">En cours</option>
          <option value="STOPPED">Arrêté</option>
          <option value="REFUSED">Refusé</option>
        </select>

        <select
          className="block w-fit px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
          value={filters.company_annonce_type}
          onChange={(event) => setFilters((prevFilters) => ({ ...prevFilters, company_annonce_type: event.target.value, page: 1 }))}>
          <option value="">Tous les types d'annonce</option>
          <option value="levee">Levée de fonds</option>
          <option value="cession">Cession</option>
        </select>

        <select
          className="block w-fit px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
          value={filters.nda_user_from_signed}
          onChange={(event) => setFilters((prevFilters) => ({ ...prevFilters, nda_user_from_signed: event.target.value, page: 1 }))}>
          <option value="">NDA Demandeur - Tous</option>
          <option value="true">NDA Demandeur - Signé</option>
          <option value="false">NDA Demandeur - Non signé</option>
        </select>

        <select
          className="block w-fit px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
          value={filters.nda_user_to_signed}
          onChange={(event) => setFilters((prevFilters) => ({ ...prevFilters, nda_user_to_signed: event.target.value, page: 1 }))}>
          <option value="">NDA Contacté - Tous</option>
          <option value="true">NDA Contacté - Signé</option>
          <option value="false">NDA Contacté - Non signé</option>
        </select>
      </div>
      {loading ? (
        <Loader />
      ) : (
        <section className="w-full py-4 flex flex-col">
          <div className="overflow-x-auto">
            <Table
              sort={filters.sort}
              onSort={(key) => {
                setFilters({ ...filters, sort: { [key]: filters.sort[key] === 1 ? -1 : 1 } });
              }}
              header={HEADER}
              loading={loading}
              noResultRender={<div className="w-full bg-white text-center p-2 my-5 rounded shadow-sm">Aucun résultat trouvé</div>}>
              {discussions.map((discussion, index) => (
                <tr
                  onClick={() => navigate(`/discussion/${discussion._id}`)}
                  className="bg-white odd:bg-gray-50 hover:bg-gray-100 h-14 rounded-lg cursor-pointer border hover:border-gray-400"
                  key={discussion._id}>
                  <td className="p-2 text-center font-medium text-gray-500 w-16">{(filters.page - 1) * filters.limit + index + 1}</td>
                  <td className="p-2 max-w-xs truncate">
                    {discussion.user_from_first_name} {discussion.user_from_last_name}
                  </td>
                  <td className="p-2 max-w-xs truncate">
                    {discussion.user_to_first_name} {discussion.user_to_last_name}
                  </td>
                  <td className="p-2 max-w-xs truncate" title={discussion.company_name}>
                    {discussion.company_name || "-"}
                  </td>
                  <td className="p-2 max-w-xs truncate">{formatAnnonceType(discussion.company_annonce_type)}</td>
                  <td className="p-2 max-w-xs truncate" title={discussion.company_annonce_title}>
                    {discussion.company_annonce_title || "-"}
                  </td>
                  <td className="p-2 max-w-xs truncate" title={discussion.investor_name}>
                    {discussion.investor_name || "-"}
                  </td>
                  <td className="p-2 text-center">
                    <NDAStatus signed={discussion.nda_user_from_signed} />
                  </td>
                  <td className="p-2 text-center">
                    <NDAStatus signed={discussion.nda_user_to_signed} />
                  </td>
                  <td className="p-2 max-w-xs truncate">{formatDateTime(discussion.created_at)}</td>
                  <td className="p-2 max-w-xs truncate">{formatDateTime(discussion.updated_at)}</td>
                  <td className="p-2 max-w-xs truncate">
                    <span
                      className={`text-xs rounded-md text-center py-1 px-3 ${
                        discussion.status === "PENDING"
                          ? "bg-yellow-300"
                          : discussion.status === "ONGOING"
                            ? "bg-green-300"
                            : discussion.status === "ACCEPTED"
                              ? "bg-blue-300"
                              : "bg-red-300"
                      }`}>
                      {discussion.status}
                    </span>
                  </td>
                </tr>
              ))}
            </Table>
          </div>
        </section>
      )}
      <Pagination page={filters.page} setPage={(page) => setFilters({ ...filters, page })} last={Math.ceil(total / filters.limit)} />
    </div>
  );
};

export default List;
